import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './css/navbar.css';
import logo from './images/logo.png';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const servicesDropdownRef = useRef(null);
  const [contactDetails, setContactDetails] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'ar' ? 'en' : 'ar');
  };

  // Services data from ExploreServices component
  const services = [
    { id: 1, title: t('services.1') },
    { id: 2, title: t('services.2') },
    { id: 3, title: t('services.3') },
    { id: 4, title: t('services.4') },
    { id: 5, title: t('services.5') },
    { id: 6, title: t('services.6') },
    { id: 7, title: t('services.7') },
    { id: 8, title: t('services.8') }
  ];

  useEffect(() => {
    loadContactDetails();
    loadSocialLinks();
  }, []);

  const loadContactDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/contact-details`);
      const data = await response.json();
      if (data.success) {
        setContactDetails(data.data);
      }
    } catch (error) {
      console.error('Error loading contact details:', error);
    }
  };

  const loadSocialLinks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.acpca.net/api'}/social-media`);
      const data = await response.json();
      if (data.success) {
        setSocialLinks(data.data);
      }
    } catch (error) {
      console.error('Error loading social links:', error);
    }
  };

  const getContactDetail = (type) => {
    const detail = contactDetails.find(d => d.contact_type === type);
    if (!detail) return null;
    return {
      value: language === 'ar' ? (detail.value_ar || detail.value_en) : (detail.value_en || detail.value_ar),
      url: detail.url
    };
  };

  const getSocialLink = (platform) => {
    return socialLinks.find(link => link.platform.toLowerCase() === platform.toLowerCase());
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };

    if (isServicesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isServicesDropdownOpen]);

  return (
    <nav>
      <div className='upper-nav'>
        <div className='container'>
          <div className='upper-content'>
            <div className='left-content'>
              {(() => {
                const email = getContactDetail('email');
                const phone = getContactDetail('phone');
                const whatsapp = getContactDetail('whatsapp');
                return (
                  <>
                    {email && (
                      <>
                        <a href={email.url || `mailto:${email.value}`}>{email.value}</a>
                        <span></span>
                      </>
                    )}
                    {phone && (
                      <>
                        <a href={phone.url || `tel:${phone.value}`}>{phone.value}</a>
                        <span></span>
                      </>
                    )}
                    {whatsapp && (
                      <a href={whatsapp.url || `https://wa.me/${whatsapp.value.replace(/[^0-9]/g, '')}`}>{whatsapp.value}</a>
                    )}
                  </>
                );
              })()}
            </div>

            <div className='right-content'>
              {(() => {
                const facebook = getSocialLink('facebook');
                const twitter = getSocialLink('twitter');
                const instagram = getSocialLink('instagram');
                return (
                  <>
                    {facebook && (
                      <>
                        <a href={facebook.url} target="_blank" rel="noopener noreferrer">{t('navbar.facebook')}</a>
                        <span></span>
                      </>
                    )}
                    {twitter && (
                      <>
                        <a href={twitter.url} target="_blank" rel="noopener noreferrer">{t('navbar.twitter')}</a>
                        <span></span>
                      </>
                    )}
                    {instagram && (
                      <a href={instagram.url} target="_blank" rel="noopener noreferrer">{t('navbar.instagram')}</a>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      <div className='lower-nav'>
        <div className='container'>
          <div className='lower-content'>
            <div className='logo'>
              <Link to='/' onClick={() => setIsMenuOpen(false)}>
                <img src={logo} alt='logo' />
              </Link>
            </div>

            {/* Menu Toggle Button for Mobile/Tablet */}
            <button 
              className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            {/* Menu */}
            <div className={`menu ${isMenuOpen ? 'menu-open' : ''}`}>
              <Link to='/' onClick={() => setIsMenuOpen(false)}>{t('navbar.home')}</Link>
              <Link to='/why-choose-us' onClick={() => setIsMenuOpen(false)}>{t('navbar.whyChooseUs')}</Link>
              <Link to='/about' onClick={() => setIsMenuOpen(false)}>{t('navbar.about')}</Link>
              <Link to='/organizational-structure' onClick={() => setIsMenuOpen(false)}>{t('pages.organizationalStructure')}</Link>
              
              {/* Services with dropdown */}
              <div 
                className="menu-services-item" 
                ref={servicesDropdownRef}
              >
                <Link 
                  to='' 
                  onClick={(e) => {
                    e.preventDefault();
                    setIsServicesDropdownOpen(!isServicesDropdownOpen);
                  }}
                  className="services-link"
                >
                  {t('navbar.services')}
                  <i className={`las ${isServicesDropdownOpen ? 'la-angle-up' : 'la-angle-down'}`}></i>
                </Link>
                <div className={`services-dropdown ${isServicesDropdownOpen ? 'open' : ''}`}>
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/service/${service.id}`}
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsServicesDropdownOpen(false);
                      }}
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              </div>

              <Link to='/contact' onClick={() => setIsMenuOpen(false)}>{t('navbar.contact')}</Link>
              <Link to='' onClick={(e) => { e.preventDefault(); toggleLanguage(); setIsMenuOpen(false); }}>
                <i className="las la-globe"></i> {language === 'ar' ? 'En' : 'Ar'}
              </Link>
              
              {/* Contact button inside mobile menu */}
              <Link to="/contact" className='mobile-contact-btn' onClick={() => setIsMenuOpen(false)}>
                {t('navbar.contactUs')}
              </Link>
            </div>

            {/* Desktop Contact Button */}
            <div className='contact-info-btn'>
              <Link to="/contact">{t('navbar.contactUs')}</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {isMenuOpen && <div className="menu-overlay" onClick={toggleMenu}></div>}
    </nav>
  );
};

export default Navbar;